import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useEffect, useMemo, useRef, useState, type ReactNode } from 'react';
import { createPortal } from 'react-dom';
import { useAppStore } from '../../app/appStore';
import { useAuthStore } from '../auth/authStore';
import { enterRaffleEvent, fetchRaffleData, fetchRaffleResult } from '../wallet/api';
import { useI18nStore } from '../../shared/i18n/i18nStore';
import { currentUnixSeconds, serverEpochSeconds } from '../../shared/lib/raffleCountdown';
import { canonicalRaffleEventId } from '../../shared/lib/raffleResultTarget';
import { raffleReadsEnabled, raffleViewerMode } from '../../shared/raffle/viewerMode';
import { GuestAuthSheet } from '../auth/GuestAuthSheet';
import { GuestJoinFooter } from '../auth/GuestJoinFooter';
import {
  RaffleRegisterSheet,
  RaffleParticipantsSheet,
  RaffleResultDetailSheet,
  findTargetRaffleResult,
} from '../../pages/tabs/WalletPage';

type ParticipantsReturn = 'register' | null;

/**
 * Global raffle flow overlay. The register / participants / result sheets used
 * to render only inside WalletPage; they now render over whichever page is
 * active (Sorteios especially), driven entirely by the store's
 * walletTicketsTargetView. This component owns the orchestration (raffle query,
 * countdown timer, enter mutation) that WalletPage previously held; the sheet
 * bodies and helpers still live in WalletPage and are imported here.
 */
export function RaffleFlowOverlay() {
  const token = useAuthStore((state) => state.token);
  const guestMode = useAuthStore((state) => state.guestMode);
  const user = useAuthStore((state) => state.user);
  const t = useI18nStore((state) => state.t);
  const walletTicketsTargetView = useAppStore((state) => state.walletTicketsTargetView);
  const walletTicketsResultId = useAppStore((state) => state.walletTicketsResultId);
  const setWalletTicketsTarget = useAppStore((state) => state.setWalletTicketsTarget);
  const setWalletTicketsReturnTarget = useAppStore((state) => state.setWalletTicketsReturnTarget);
  const dismissWalletTicketsFlow = useAppStore((state) => state.dismissWalletTicketsFlow);
  const setRoute = useAppStore((state) => state.setRoute);
  const setActiveProfileUserId = useAppStore((state) => state.setActiveProfileUserId);
  const setProfileReturn = useAppStore((state) => state.setProfileReturn);
  const setArchiveReturn = useAppStore((state) => state.setArchiveReturn);
  const setActiveArchiveRoundNumber = useAppStore((state) => state.setActiveArchiveRoundNumber);
  const showToast = useAppStore((state) => state.showToast);
  const clearGuest = useAuthStore((state) => state.clearGuest);
  const logout = useAuthStore((state) => state.logout);
  const viewer = raffleViewerMode(token, guestMode);
  const isGuest = viewer === 'guest';
  const [guestAuthOpen, setGuestAuthOpen] = useState(false);
  const leaveGuest = (nextRoute: 'auth.register' | 'auth.login') => {
    setGuestAuthOpen(false);
    dismissWalletTicketsFlow();
    clearGuest();
    logout();
    setRoute(nextRoute);
  };
  // A guest tapping a person cannot open a profile (that API needs an account);
  // the honest answer is the sign-up sheet, as the lobby feed already does.
  const guestTap = () => setGuestAuthOpen(true);

  const active = !!walletTicketsTargetView;
  // The overlay stays mounted between flows; a back-button / Escape dismissal
  // bypasses the guest sheet's own close handler, so clear it here or it
  // reappears unprompted on the next sheet.
  useEffect(() => {
    if (!active) setGuestAuthOpen(false);
  }, [active]);
  // The page currently under the sheet — captured when leaving for a side
  // excursion (profile / round verify) so its close can restore this exact spot.
  const sheetOrigin = () => {
    const { route, activeRoomId } = useAppStore.getState();
    return { route, roomId: activeRoomId };
  };
  const [participantsReturn, setParticipantsReturn] = useState<ParticipantsReturn>(null);
  const [deviceNow, setDeviceNow] = useState(currentUnixSeconds);
  const [clockOffset, setClockOffset] = useState(0);
  const missingToastShown = useRef(new Set<string>());
  const queryClient = useQueryClient();

  const raffleQuery = useQuery({
    queryKey: ['raffle', token],
    queryFn: () => fetchRaffleData(token || ''),
    enabled: raffleReadsEnabled(viewer) && active,
    staleTime: 15_000,
  });

  // Schedule chips carry the immutable event UUID. Fetch that public result
  // directly instead of assuming the viewer also has an entry in the draw.
  // Legacy timestamp-based deep links still fall back to recent_events below.
  const directResultId = canonicalRaffleEventId(walletTicketsResultId);
  const raffleResultQuery = useQuery({
    queryKey: ['raffle-result', token, directResultId],
    queryFn: () => fetchRaffleResult(token || '', directResultId || ''),
    enabled: raffleReadsEnabled(viewer) && walletTicketsTargetView === 'result' && !!directResultId,
    staleTime: 5 * 60_000,
    retry: false,
  });

  const raffleEnterMutation = useMutation({
    mutationFn: (count: number) => {
      const event = raffleQuery.data?.event;
      if (!token || !event) throw new Error('UNAUTHORIZED');
      return enterRaffleEvent(token, String(event.event_id || event.id || ''), count);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['raffle', token] });
      queryClient.invalidateQueries({ queryKey: ['wallet', token] });
    },
  });

  // 1s countdown clock — only ticking while a sheet is on screen.
  useEffect(() => {
    if (!active) return undefined;
    const tick = () => setDeviceNow(currentUnixSeconds());
    tick();
    const timer = window.setInterval(tick, 1_000);
    return () => window.clearInterval(timer);
  }, [active]);

  // Countdowns follow the server clock (device + offset) so a wrong device
  // clock or a non-Brazil timezone still shows correct time remaining.
  const serverTime = serverEpochSeconds(raffleQuery.data?.server_time);
  useEffect(() => {
    if (serverTime) setClockOffset(serverTime - currentUnixSeconds());
  }, [serverTime]);
  const nowSeconds = deviceNow + clockOffset;

  // The register↔participants back affordance only exists while participants is
  // the active view AND we arrived there from register. Any other transition
  // clears it so a later direct entry (e.g. from the ledger) shows no back arrow.
  useEffect(() => {
    if (walletTicketsTargetView !== 'participants') setParticipantsReturn(null);
  }, [walletTicketsTargetView]);

  const fallbackResultEvent = useMemo(
    () => (walletTicketsTargetView === 'result'
      ? findTargetRaffleResult(raffleQuery.data, walletTicketsResultId, user?.id)
      : null),
    [raffleQuery.data, user?.id, walletTicketsResultId, walletTicketsTargetView],
  );
  const resultEvent = raffleResultQuery.data || fallbackResultEvent;

  // Only report a missing result after both the direct public lookup and the
  // legacy recent-events fallback have finished. A non-participant is no
  // longer mislabelled as having opened an expired draw.
  useEffect(() => {
    if (walletTicketsTargetView !== 'result' || resultEvent) return;
    const targetId = walletTicketsResultId || '';
    const directLookupPending = !!directResultId && (raffleResultQuery.isLoading || raffleResultQuery.isFetching);
    if (targetId && !directLookupPending && !raffleQuery.isLoading && !raffleQuery.isFetching && !missingToastShown.current.has(targetId)) {
      missingToastShown.current.add(targetId);
      showToast(t('raffleResultUnavailable'), 'info');
      setWalletTicketsTarget(null, null, { replace: true });
    }
  }, [directResultId, raffleQuery.isFetching, raffleQuery.isLoading, raffleResultQuery.isFetching, raffleResultQuery.isLoading, resultEvent, setWalletTicketsTarget, showToast, t, walletTicketsResultId, walletTicketsTargetView]);

  const withGuestSheet = (sheet: ReactNode) => (
    <>
      {sheet}
      {guestAuthOpen && typeof document !== 'undefined' ? createPortal(
        // The participants / register sheets portal to document.body, and
        // useModalLayer makes #root inert while they're open — this sheet
        // must live in document.body too or it paints on top but can't be
        // clicked. over-raffle-sheet (130) keeps it above their z-index 120
        // backdrops.
        <GuestAuthSheet
          backdropClassName="sheet-backdrop nested-sheet-backdrop over-raffle-sheet"
          onSignup={() => leaveGuest('auth.register')}
          onLogin={() => leaveGuest('auth.login')}
          onClose={() => setGuestAuthOpen(false)}
        />,
        document.body,
      ) : null}
    </>
  );

  if (!active) return null;

  if (walletTicketsTargetView === 'register' && isGuest) {
    return (
      <GuestAuthSheet
        onSignup={() => leaveGuest('auth.register')}
        onLogin={() => leaveGuest('auth.login')}
        onClose={() => dismissWalletTicketsFlow()}
      />
    );
  }

  if (walletTicketsTargetView === 'register' && raffleQuery.data?.event) {
    return withGuestSheet(
      <RaffleRegisterSheet
        data={raffleQuery.data}
        userId={user?.id}
        pending={raffleEnterMutation.isPending}
        error={raffleEnterMutation.isError}
        nowSeconds={nowSeconds}
        onClose={() => {
          dismissWalletTicketsFlow();
          raffleEnterMutation.reset();
          raffleQuery.refetch();
        }}
        onParticipants={() => {
          setParticipantsReturn('register');
          setWalletTicketsTarget('participants');
        }}
        onSubmit={(count) => raffleEnterMutation.mutateAsync(count)}
        onRules={(targetId) => {
          setWalletTicketsTarget(null);
          // "earn tickets" → the rewards/activities hub; anything else → the
          // dedicated Draws fairness page.
          setRoute(targetId === 'rules-raffle-tickets' ? 'rewards' : 'rules');
        }}
      />,
    );
  }

  if (walletTicketsTargetView === 'participants' && raffleQuery.data?.event) {
    return withGuestSheet(
      <RaffleParticipantsSheet
        data={raffleQuery.data}
        userId={user?.id}
        onBack={participantsReturn === 'register' ? () => {
          setParticipantsReturn(null);
          setWalletTicketsTarget('register');
        } : undefined}
        onOpenProfile={isGuest ? () => guestTap() : (profileUserId) => {
          setParticipantsReturn(null);
          setProfileReturn({ view: 'participants', resultId: null, origin: sheetOrigin() });
          setActiveProfileUserId(String(profileUserId));
          setRoute('leaderboard');
        }}
        guestFooter={isGuest ? <GuestJoinFooter onSignup={() => leaveGuest('auth.register')} /> : null}
        guest={isGuest}
        onClose={() => {
          setParticipantsReturn(null);
          dismissWalletTicketsFlow();
        }}
      />,
    );
  }

  if (walletTicketsTargetView === 'result' && resultEvent) {
    return withGuestSheet(
      <RaffleResultDetailSheet
        event={resultEvent}
        userId={user?.id}
        onClose={() => {
          dismissWalletTicketsFlow();
        }}
        onTickets={() => {
          setWalletTicketsTarget(null);
          setWalletTicketsReturnTarget(null);
        }}
        onRules={() => {
          setWalletTicketsTarget(null);
          setRoute('rules');
        }}
        onVerifyAnchor={(roundNumber) => {
          setArchiveReturn({ resultId: walletTicketsResultId, origin: sheetOrigin() });
          setWalletTicketsTarget(null);
          setActiveArchiveRoundNumber(roundNumber);
          setRoute('archive');
        }}
        onOpenProfile={isGuest ? () => guestTap() : (profileUserId) => {
          setProfileReturn({ view: 'result', resultId: walletTicketsResultId, origin: sheetOrigin() });
          setActiveProfileUserId(String(profileUserId));
          setRoute('leaderboard');
        }}
        guestFooter={isGuest ? <GuestJoinFooter onSignup={() => leaveGuest('auth.register')} /> : null}
        guest={isGuest}
      />,
    );
  }

  return null;
}
